import {River, Heading, Text, Button, Link, Image, Grid, Stack, CTABanner, AnimationProvider} from '@primer/react-brand'
import type {ReactNode} from 'react'

function FeatureItems(items: Array<{title: string; content: ReactNode[]}>) {
  return (
    <div className="list-box my-md-8">
      <AnimationProvider>
        <Stack
          padding={{
            narrow: 'condensed',
            regular: 'spacious',
            wide: 'spacious',
          }}
        >
          <Grid>
            {items.map(({title, content}, index) => (
              <Grid.Column key={index} span={{large: 6}}>
                <Stack key={index} animate="scale-in-up">
                  <Heading as="h3" size="6" weight="semibold">
                    {title}
                  </Heading>
                  {content.map((item, i) => (
                    <div key={i}>{item}</div>
                  ))}
                </Stack>
              </Grid.Column>
            ))}
          </Grid>
        </Stack>
      </AnimationProvider>
    </div>
  )
}

export function DiversityIndex() {
  return (
    <div>
      <div className="color-bg-subtle py-6 py-lg-12" data-hpc>
        <Grid>
          <Grid.Column span={{large: 9}}>
            <Stack padding="none" className="py-6">
              <Heading as="h1" weight="extrabold">
                Global diversity, inclusion, and belonging at GitHub
              </Heading>
              <Text variant="muted" size="400" weight="light">
                We are the world&lsquo;s largest developer community and value the potential of a diverse open-source
                community. We prioritize promoting diversity and inclusion across our platform, people, philanthropy,
                and policy. See below for more information on our global strategy.
              </Text>
            </Stack>
          </Grid.Column>
          <Grid.Column span={{large: 3}} className="hero-decoration-column">
            <div className="hero-decoration">
              {[
                {backgroundColor: 'var(--base-color-scale-purple-5)', top: 0, left: 0},
                {backgroundColor: 'var(--base-color-scale-yellow-1)', top: '50%', left: '20%'},
                {backgroundColor: 'var(--base-color-scale-gray-2)', bottom: 0, left: '50%'},
                {backgroundColor: 'var(--base-color-scale-pink-3)', top: '10%', left: '75%'},
                {backgroundColor: 'var(--base-color-scale-orange-2)', top: '75%', right: 0},
              ].map((style, index) => (
                <div key={index} className="color-shadow-medium" style={style} />
              ))}
            </div>
          </Grid.Column>
        </Grid>
      </div>

      <Grid>
        <Grid.Column>
          <Stack gap="spacious" padding="none">
            <AnimationProvider>
              <River imageTextRatio="60:40">
                <River.Visual>
                  <Image
                    src="/images/modules/site/about/diversity/2023/diversity-platform.jpg"
                    alt="An illustration showing two people's faces"
                  />
                </River.Visual>
                <River.Content animate="slide-in-left">
                  <Heading as="h2" size="3" weight="extrabold">
                    Platform
                  </Heading>
                  <Text variant="muted" size="300" weight="light">
                    Our goal is to speed up human progress by enabling developers to collaborate regardless of their
                    skills or experience. We prioritize global accessibility through localization, outreach, community
                    building, compliance.
                  </Text>
                </River.Content>
              </River>
            </AnimationProvider>

            <AnimationProvider>
              <Grid>
                <Grid.Column span={{medium: 9, large: 8, xlarge: 7}}>
                  <Text
                    as="p"
                    size="500"
                    weight="semibold"
                    className="text-gradient text-gradient-light-purple-red"
                    animate="slide-in-right"
                  >
                    Software development should be accessible, inclusive, and sustainable, so that developers around the
                    world learn, contribute, grow, and feel like they belong.
                  </Text>
                </Grid.Column>
              </Grid>
            </AnimationProvider>

            {FeatureItems([
              {
                title: 'Provide an on ramp for the next generation of developers',
                content: [
                  <Text as="p" variant="muted" weight="light" key="next-generation-1">
                    The Major League Hacking (MLH){' '}
                    <a
                      className="Link--inTextBlock"
                      href="https://github.blog/2020-05-04-announcing-the-mlh-fellowship-powered-by-github/"
                    >
                      MLH Fellowship, Powered by GitHub
                    </a>{' '}
                    is a remote program now available in over 32 regions that matches students with open source projects
                    and mentors.
                  </Text>,
                  <Text as="p" variant="muted" weight="light" key="next-generation-2">
                    <a className="Link--inTextBlock" href="https://education.github.com/">
                      GitHub Education
                    </a>{' '}
                    has served 2.5 million students globally and provides students, teachers, and schools with access to
                    powerful tools that support the growth and learning of the next generation of developers.
                  </Text>,
                ],
              },
              {
                title: 'Use inclusive language',
                content: [
                  <Text as="p" variant="muted" weight="light" key="inclusive-1">
                    We coordinated with industry partners and the Git community more broadly to drive the work to{' '}
                    <a className="Link--inTextBlock" href="https://github.com/github/renaming">
                      change default branch name for new GitHub repositories from master to main
                    </a>
                    . We are now working with a number organizations to make this an industry-wide change.
                  </Text>,
                  <Text as="p" variant="muted" weight="light" key="inclusive-2">
                    <a
                      className="Link--inTextBlock"
                      href="https://github.blog/changelog/2020-09-29-new-languages-for-github-for-mobile/"
                    >
                      GitHub for mobile is available in English, Brazilian Portuguese, Japanese, Simplified Chinese, and
                      Spanish
                    </a>
                    . Now more developers can organize tasks, respond to issues, and review and merge pull requests on
                    the go – in more languages than ever.
                  </Text>,
                ],
              },
              {
                title: 'Contribute where it counts',
                content: [
                  <Text as="p" variant="muted" weight="light" key="contribute-1">
                    <a className="Link--inTextBlock" href="https://github.com/sponsors">
                      GitHub Sponsors
                    </a>{' '}
                    allows the developer community to financially support the people and organizations who design,
                    build, and maintain the open source projects they depend on, directly on GitHub.
                  </Text>,
                  <Text as="p" variant="muted" weight="light" key="contribute-2">
                    In 2020, we joined forces with Microsoft and LinkedIn in a{' '}
                    <a
                      className="Link--inTextBlock"
                      href="https://blogs.microsoft.com/blog/2020/06/30/microsoft-launches-initiative-to-help-25-million-people-worldwide-acquire-the-digital-skills-needed-in-a-covid-19-economy/"
                    >
                      global skills initiative
                    </a>{' '}
                    aimed at bringing more digital skills to 25 million people worldwide.
                  </Text>,
                ],
              },
              {
                title: 'Amplify underrepresented voices',
                content: [
                  <Text as="p" variant="muted" weight="light" key="amplify-1">
                    <a className="Link--inTextBlock" href="https://github.com/readme">
                      The ReadME Project
                    </a>{' '}
                    provides a spotlight to a diverse range of voices in the global open source community: the
                    maintainers, developers, and teams whose powerful contributions move the world forward every day.
                  </Text>,
                  <Text as="p" variant="muted" weight="light" key="amplify-2">
                    <a className="Link--inTextBlock" href="https://stars.github.com/">
                      GitHub Stars
                    </a>{' '}
                    is a platform for our most influential developers to showcase their work, reach more people, and
                    shape the future of GitHub.
                  </Text>,
                ],
              },
            ])}
          </Stack>

          <div className="py-md-5">
            <AnimationProvider>
              <River imageTextRatio="60:40" align="end">
                <River.Visual>
                  <Image
                    src="/images/modules/site/about/diversity/2023/diversity-people.jpg"
                    alt="A grid of diverse group of people"
                  />
                </River.Visual>
                <River.Content animate="slide-in-right">
                  <Heading as="h2" size="3" weight="extrabold">
                    People
                  </Heading>
                  <Text variant="muted" size="300" weight="light">
                    We want our teams at GitHub to reflect the global developer community. In order to archive so, we
                    invest in building teams with diverse perspectives, backgrounds, and life experiences.
                  </Text>
                  <Link href="/about/diversity/communities-of-belonging" className="no-underline">
                    Learn more about people and culture
                  </Link>
                </River.Content>
              </River>
            </AnimationProvider>

            <div className="py-6">
              <AnimationProvider>
                <Grid>
                  <Grid.Column span={{medium: 9, large: 8, xlarge: 7}}>
                    <Text
                      as="p"
                      size="500"
                      weight="semibold"
                      className="text-gradient text-gradient-light-pink-blue"
                      animate="slide-in-right"
                    >
                      Our goal at GitHub is to create a culture fueled by people who are passionate about building a
                      global community of developers and doing the best work of their lives along the way.
                    </Text>
                  </Grid.Column>
                </Grid>
              </AnimationProvider>
            </div>

            {FeatureItems([
              {
                title: 'Apply data to our hiring process',
                content: [
                  <Text as="p" variant="muted" weight="light" key="hiring-1">
                    We set and track ambitious hiring, retention, and promotional goals for underrepresented
                    communities.
                  </Text>,
                ],
              },
              {
                title: 'Hold ourselves accountable',
                content: [
                  <Text as="p" variant="muted" weight="light" key="accountable-1">
                    We share our annual Diversity Report, which brings transparency to diversity efforts, like
                    representation in tech roles and in leadership.
                  </Text>,
                ],
              },
              {
                title: 'Promote continuous learning',
                content: [
                  <Text as="p" variant="muted" weight="light" key="learning-1">
                    We invest in unconscious bias, privilege, and allyship training for all employees. This empowers
                    everyone to develop awareness, exercise curiosity, and demonstrate courage.
                  </Text>,
                ],
              },
              {
                title: 'Collaborate and co-create',
                content: [
                  <Text as="p" variant="muted" weight="light" key="collaborate-1">
                    We closely collaborate with our{' '}
                    <a className="Link--inTextBlock" href="https://github.com/about/diversity/communities-of-belonging">
                      Communities of Belonging (CoB)
                    </a>{' '}
                    and with our Inclusion Advisory Council (IAC) to guide and inform our priorities.
                  </Text>,
                ],
              },
              {
                title: 'Provide support for Hubbers',
                content: [
                  <Text as="p" variant="muted" weight="light" key="support-1">
                    We offer competitive benefits, including access to mental health services, a wellness stipend,
                    $2,000 USD a year for learning and development, and back-up child care and adult/elder care.
                  </Text>,
                ],
              },
            ])}
          </div>

          <div className="py-md-5">
            <AnimationProvider>
              <River imageTextRatio="60:40">
                <River.Visual>
                  <Image
                    src="/images/modules/site/about/diversity/2023/diversity-philanthropy.jpg"
                    alt="A mix of images of GitHub employees at events"
                  />
                </River.Visual>
                <River.Content animate="slide-in-right">
                  <Heading as="h2" size="3" weight="extrabold">
                    Philanthropy
                  </Heading>
                  <Text variant="muted" size="300" weight="light">
                    We support non-profits and social sector organizations via employee and company donations, service
                    projects, and special programs. Our{' '}
                    <a className="Link--inTextBlock" href="https://socialimpact.github.com/">
                      Social Impact Team
                    </a>{' '}
                    connects organizations with Hubbers, our and the open source community.
                  </Text>
                  <Link href="https://socialimpact.github.com/" className="no-underline">
                    Learn more about GitHub Social Impact
                  </Link>
                </River.Content>
              </River>
            </AnimationProvider>

            {FeatureItems([
              {
                title: 'Tech for Social Good',
                content: [
                  <Text as="p" variant="muted" weight="light" key="social-good-1">
                    We created{' '}
                    <a className="Link--inTextBlock" href="https://socialimpact.github.com/">
                      Tech for Social Good
                    </a>{' '}
                    sector organizations working on issues that developers and their communities, like the COVID-19
                    public health response.
                  </Text>,
                ],
              },
              {
                title: 'The Tech Pipeline',
                content: [
                  <Text as="p" variant="muted" weight="light" key="pipeline-1">
                    We’ve partnered with seven organizations to create the Tech Pipeline to make sure the next
                    generation developers includes the most underserved communities in the world.
                  </Text>,
                ],
              },
              {
                title: 'Hubber Giving',
                content: [
                  <Text as="p" variant="muted" weight="light" key="giving-1">
                    Hubbers are incredibly generous and give their time, talent, and expertise to nonprofits. In order
                    to further support those efforts, we match up to $15,000 USD in donations annually. As part of every
                    team retreat or offsite, we integrate volunteer opportunities and provide 40 hours of volunteer
                    credits per year to all Hubbers.
                  </Text>,
                ],
              },
            ])}
          </div>

          <div className="py-md-5">
            <AnimationProvider>
              <River imageTextRatio="60:40" align="end">
                <River.Visual>
                  <Image
                    src="/images/modules/site/about/diversity/2023/diversity-policy.jpg"
                    alt="Screenshots of online articles about GitHub's advocacy"
                  />
                </River.Visual>
                <River.Content animate="slide-in-left">
                  <Heading as="h2" size="3" weight="extrabold">
                    Policy
                  </Heading>
                  <Text variant="muted" size="300" weight="light">
                    We advocate for inclusive opportunities through law, regulation, and social action on our platform.
                    Our focus on local and global initiatives includes supporting DACA and joining tech organizations in
                    amicus briefs for international students to remain in the US, recognizing policy&lsquo;s impact on
                    who can become a developer and what they can build.
                  </Text>
                  <Link href="/about/developer-policy" className="no-underline">
                    Learn more about our policy work
                  </Link>
                </River.Content>
              </River>
            </AnimationProvider>

            {FeatureItems([
              {
                title: 'Protecting privacy and security',
                content: [
                  <Text as="p" variant="muted" weight="light" key="privacy-security-1">
                    We continually push to foster and protect developers’ abilities to collaborate and drive change by
                    contributing to projects that protect the privacy and security of people across the globe,
                    especially at risk populations.
                  </Text>,
                ],
              },
              {
                title: 'Promote civic engagement',
                content: [
                  <Text as="p" variant="muted" weight="light" key="privacy-security-1">
                    Finally, we promote civic engagement by developers globally, including making sure that Hubbers have
                    paid time off to vote and serve as election workers.
                  </Text>,
                ],
              },
            ])}
          </div>

          <div className="my-10">
            <Stack padding="none" gap="spacious">
              <CTABanner align="center" className="width-full">
                <CTABanner.Heading size="3" weight="extrabold">
                  Join us on our path to diversity, inclusion, and belonging for all
                </CTABanner.Heading>
                <CTABanner.ButtonGroup>
                  <Button
                    as="a"
                    href="https://github.careers"
                    className="no-underline"
                    size="large"
                    {...{
                      'data-analytics-event': JSON.stringify({
                        category: 'Get Copilot for Business',
                        action: 'click to Get Copilot for Business',
                        label: 'ref_cta:Get Copilot for Business;',
                      }),
                    }}
                  >
                    See career opportunities
                  </Button>
                </CTABanner.ButtonGroup>
              </CTABanner>
            </Stack>
          </div>
        </Grid.Column>
      </Grid>
    </div>
  )
}

try{ FeatureItems.displayName ||= 'FeatureItems' } catch {}
try{ DiversityIndex.displayName ||= 'DiversityIndex' } catch {}